// externals
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { SendDialogTypes } from '@makemydeal/dr-dash-types';
import { dealerSelectors } from '@makemydeal/dr-shared-store';

// hooks
import { useCustomerUI } from '../customerUI/useCustomerUI';
import { useValidationGuard } from './useValidationGuard';

// interfaces/types
import type { SendToCustomerDialogProps } from '../sendToCustomerDialog/SendToCustomerDialog.interstate';

/**
 * Props to spread onto `SendToCustomerDialogInterstate`. The consumer owns rendering the dialog so one activity
 * can be shared from more than one surface without two dialogs fighting over a single piece of state.
 */
export type ActivityShareLinkDialogProps = Pick<
    SendToCustomerDialogProps,
    'show' | 'onHide' | 'shareUrl' | 'dialogHeader' | 'dialogPrompt' | 'dialogType'
>;

export type ActivityShareLink = {
    shareLink: () => void;
    dialogProps: ActivityShareLinkDialogProps;
};

type ActivityShareLinkConfig = {
    getShareUrl: (state: StateTree) => string;
    analyticsAction: () => { type: string };
    dialogHeader: string;
    dialogPrompt: string;
    dialogType: SendDialogTypes;
};

/**
 * The validation guard lives HERE rather than at the call site so there is exactly one guard per click. Callers
 * must not wrap `shareLink` in another `useValidationGuard`, and the Share menu's action types must stay out of
 * `EXIT_ACTION_TYPES` in `baseDealFooter/Actions.interstate.tsx` - either double-guards the same click.
 */
const useActivityShareLink = (config: ActivityShareLinkConfig): ActivityShareLink => {
    const { getShareUrl, analyticsAction, dialogHeader, dialogPrompt, dialogType } = config;

    const dispatch = useDispatch();
    const guard = useValidationGuard();
    const { showCustomerCard } = useCustomerUI();
    const crmIntegrationToggle = useSelector(dealerSelectors.getCrmIntegrationToggle);
    const shareUrl = useSelector(getShareUrl);
    const [showDialog, setShowDialog] = useState(false);

    // istanbul ignore next # this is invoked WAY down the pipeline and out of scope here. We just want to delay getting the URL until the url loads asynchronously
    const messageContentSelector = (state: any) => {
        const url = getShareUrl(state);
        return `\n${url}`;
    };

    const shareLink = useMemo(
        () =>
            guard.wrap(() => {
                dispatch(offerActionCreators.saveOffer());
                dispatch(analyticsAction());
                if (crmIntegrationToggle) {
                    showCustomerCard(true, { tab: 'inbox', messageContentSelector, waitForOfferSave: true });
                } else {
                    setShowDialog(true);
                }
            }),
        // `messageContentSelector` is omitted: it only closes over the module-level `getShareUrl`, so a stale
        // closure behaves identically.
        [dispatch, guard.wrap, crmIntegrationToggle, showCustomerCard, analyticsAction]
    );

    const onHide = useCallback(() => setShowDialog(false), []);

    return useMemo(
        () => ({
            shareLink,
            dialogProps: { show: showDialog, onHide, shareUrl, dialogHeader, dialogPrompt, dialogType }
        }),
        [shareLink, showDialog, onHide, shareUrl, dialogHeader, dialogPrompt, dialogType]
    );
};

export const useCreditAppShareLink = (): ActivityShareLink =>
    useActivityShareLink({
        getShareUrl: offerInfoSelectors.getCreditAppURL,
        analyticsAction: interactionAnalyticsActionCreators.creditAppSendLink,
        dialogHeader: 'Send Credit App to Customer',
        dialogPrompt: 'Send the customer a direct link to complete the credit application',
        dialogType: SendDialogTypes.CreditApp
    });

export const useDocUploadShareLink = (): ActivityShareLink =>
    useActivityShareLink({
        getShareUrl: offerInfoSelectors.getDocUploadURL,
        analyticsAction: interactionAnalyticsActionCreators.uploadDocsSendLink,
        dialogHeader: 'Send Document Upload Request to Customer',
        dialogPrompt: 'Send the customer a direct link to upload documents',
        dialogType: SendDialogTypes.DocUpload
    });
